import { useEffect, useState } from "react";
import { Eye, Mail, MessageCircle, ShoppingCart, TrendingUp, Globe, Send } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import AdminGate from "@/components/AdminGate";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";

const KIND_LABEL = { order: "Order", birthday: "Birthday", party: "Party", chat: "Rudi chat", contact: "Contact" };

function StatCard({ icon: Icon, label, children, testid }) {
  return (
    <div className="card-dark p-6" data-testid={testid}>
      <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-[#8a7b5e]">
        <Icon size={14} /> {label}
      </p>
      <div className="mt-3">{children}</div>
    </div>
  );
}

export default function AdminAnalytics() {
  useSeo({ title: "Admin · Stats", description: "Staff portal analytics" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/analytics")
      .then((res) => setData(res.data))
      .catch(() => setError("Could not load the stats — try again."));
  }, []);

  const v = data?.views || {};
  const e = data?.emails || {};
  const m = data?.messages || {};
  const o = data?.orders || {};
  const maxDaily = Math.max(1, ...(v.daily || []).map((d) => d.count));

  return (
    <AdminGate>
      <section className="pb-24 pt-10 sm:pt-14" data-testid="analytics-admin-page">
      <PageHeader
        tight
        pageName="Admin"
        eyebrow="Staff portal · stats"
        title="PARK STATS"
        subline="Website visits, emails the site sent, and messages visitors left — all in one place."
        image="/admin-header.jpg"
      />
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        {error && <p className="card-dark mt-10 p-6 text-sm text-red-600" data-testid="analytics-error">{error}</p>}
        {!data && !error && <p className="card-dark mt-10 p-6 text-sm text-[#8a7b5e]">Loading the stats…</p>}

        {data && (
          <>
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard icon={Eye} label="Page views" testid="analytics-views-card">
                <p className="font-display text-3xl text-black" data-testid="analytics-views-total">{v.total ?? 0}</p>
                <p className="mt-1 text-xs text-[#8a7b5e]">{v.today ?? 0} today · {v.week ?? 0} this week</p>
              </StatCard>
              <StatCard icon={Mail} label="Emails sent" testid="analytics-emails-card">
                <p className="font-display text-3xl text-black" data-testid="analytics-emails-total">{e.total ?? 0}</p>
                <p className="mt-1 text-xs text-[#8a7b5e]">{e.failed ?? 0} failed</p>
              </StatCard>
              <StatCard icon={MessageCircle} label="Visitor messages" testid="analytics-messages-card">
                <p className="font-display text-3xl text-black" data-testid="analytics-messages-total">{m.total ?? 0}</p>
                <p className="mt-1 text-xs text-[#8a7b5e]">sent through the website</p>
              </StatCard>
              <StatCard icon={ShoppingCart} label="Orders" testid="analytics-orders-card">
                <p className="font-display text-3xl text-black" data-testid="analytics-orders-total">{o.total ?? 0}</p>
                <p className="mt-1 text-xs text-[#8a7b5e]">{o.paid ?? 0} paid · R{o.revenue_paid ?? 0} collected</p>
              </StatCard>
            </div>

            <div className="card-dark mt-6 p-6" data-testid="analytics-daily-card">
              <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-[#8a7b5e]">
                <TrendingUp size={14} /> Page views — last 14 days
              </p>
              <div className="mt-4 flex h-28 items-end gap-1.5" data-testid="analytics-daily-chart">
                {(v.daily || []).length === 0 && <p className="text-xs text-[#8a7b5e]">No visits recorded yet.</p>}
                {(v.daily || []).map((d) => (
                  <div key={d.date} className="flex flex-1 flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                    <div className="w-full rounded-t bg-[#f8b818]" style={{ height: `${Math.max(6, (d.count / maxDaily) * 100)}%` }} />
                    <span className="text-[9px] font-bold text-[#8a7b5e]">{d.date.slice(5)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              <div className="card-dark p-6" data-testid="analytics-top-pages">
                <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-[#8a7b5e]"><Globe size={14} /> Most visited pages</p>
                <ul className="mt-3 space-y-1.5">
                  {(v.top_paths || []).length === 0 && <li className="text-xs text-[#8a7b5e]">Nothing yet.</li>}
                  {(v.top_paths || []).map((p) => (
                    <li key={p.path} className="flex justify-between gap-3 text-sm text-black">
                      <span className="truncate">{p.path}</span>
                      <span className="font-extrabold">{p.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="card-dark p-6" data-testid="analytics-referrers">
                <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-[#8a7b5e]">Where visitors come from</p>
                <ul className="mt-3 space-y-1.5">
                  {(v.top_referrers || []).length === 0 && <li className="text-xs text-[#8a7b5e]">No outside referrals recorded yet — visitors typed the address or found you on Google.</li>}
                  {(v.top_referrers || []).map((r) => (
                    <li key={r.referrer} className="flex justify-between gap-3 text-sm text-black">
                      <span className="truncate">{r.referrer}</span>
                      <span className="font-extrabold">{r.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="card-dark mt-6 p-6" data-testid="analytics-emails-recent">
              <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-[#8a7b5e]"><Send size={14} /> Emails the website sent</p>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[560px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-[#241c0d]/10 text-[11px] uppercase tracking-widest text-[#8a7b5e]">
                      <th className="py-2 pr-3">When</th>
                      <th className="py-2 pr-3">Kind</th>
                      <th className="py-2 pr-3">To</th>
                      <th className="py-2 pr-3">Subject</th>
                      <th className="py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(e.recent || []).length === 0 && (
                      <tr><td colSpan={5} className="py-3 text-xs text-[#8a7b5e]">No emails sent yet.</td></tr>
                    )}
                    {(e.recent || []).map((mail, i) => (
                      <tr key={`${mail.created_at}-${i}`} className="border-b border-[#241c0d]/5 text-black" data-testid={`analytics-email-row-${i}`}>
                        <td className="py-2 pr-3 text-xs text-[#8a7b5e]">{(mail.created_at || "").slice(0, 16).replace("T", " ")}</td>
                        <td className="py-2 pr-3 text-xs font-extrabold">{KIND_LABEL[mail.kind] || "Contact"}</td>
                        <td className="max-w-[180px] truncate py-2 pr-3 text-xs">{mail.to}</td>
                        <td className="max-w-[240px] truncate py-2 pr-3 text-xs">{mail.subject}</td>
                        <td className="py-2 text-xs font-extrabold">
                          {mail.status === "sent" ? <span className="text-green-600">Sent</span> : <span className="text-red-600">Failed</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card-dark mt-6 p-6" data-testid="analytics-messages-recent">
              <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest text-[#8a7b5e]"><MessageCircle size={14} /> Latest messages from visitors</p>
              <ul className="mt-3 space-y-3">
                {(m.recent || []).length === 0 && <li className="text-xs text-[#8a7b5e]">No messages yet.</li>}
                {(m.recent || []).map((msg, i) => (
                  <li key={`${msg.created_at}-${i}`} className="rounded-2xl border border-[#241c0d]/10 bg-[#faf5ea] p-4" data-testid={`analytics-message-row-${i}`}>
                    <p className="text-xs font-extrabold text-black">{msg.name || "Visitor"} <span className="font-normal text-[#8a7b5e]">· {msg.email || "no email"}{msg.phone ? ` · ${msg.phone}` : ""}</span></p>
                    <p className="mt-1 text-sm text-black">{msg.message}</p>
                    <p className="mt-1 text-[10px] text-[#8a7b5e]">{(msg.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}
      </div>
      </section>
    </AdminGate>
  );
}